import React, { useEffect, useState } from "react";
import { Modal } from "@mui/material";
import Styles from "./ChargeSelection.module.css";
import { Close } from "@mui/icons-material";
import {
  getContractCharges,
  walletAccountApi,
} from "../../../../../Services/Payments/PaymentApi";
import ConfirmationModal from "../../../../../commons/ConfirmationModal/ConfirmationModal";
import { useSelector } from "react-redux";
import CircularProgress from "@mui/material/CircularProgress";
import { Skeleton } from "@mui/material";

const ChargeSelection = ({
  open,
  handleClose,
  handleSubmit,
  details,
  loading,
  type,
  setRejectContract,
  setRejectAllTermsConfirm,
  mainScreenMatchData,
}) => {
  const activeScreen = useSelector((state) => state);
  const activeRole = useSelector((state) => state?.persistedReducer?.userType);
  const isTrader = useSelector((state) => state?.persistedReducer);
  const userEntity = isTrader?.user?.isUserEntity;
  const isSelfTraderUser = isTrader?.roles?.includes("TRADER");


  // --- STATE ---
  const [chargeData, setChargeData] = useState([]);
  const [confirmModal, setConfirmModal] = useState({
    status: false,
    header: null,
    data: null,
  });
  const [isLoadingCharges, setIsLoadingCharges] = useState(false);

  // Derived state for entity names (Counter parties)
  const entityNames = [
    ...new Set(
      chargeData
        .filter((e) => e.entityName !== "Self")
        .map((e) => e.entityName),
    ),
  ].filter((entityName) =>
    chargeData
      .filter((e) => e.entityName === entityName)
      .some((e) => e.chargeType !== "Platform"),
  );

  // --- API HELPERS ---
  const fetchWalletCharges = async (role, id, entityLabel, traderId = null) => {
    try {
      const res = await walletAccountApi({ role: role, id: id });
      const rawCharges =
        res?.data?.data?.walletAccounts?.[0]?.charges?.filter(
          (ele) => ele?.frequency === "RECURRING",
        ) || [];

      return rawCharges.map((item) => ({
        ...item,
        entityName: entityLabel,
        sourceType: "WALLET",
        consumerId: role === "consumer" ? id : null,
        generatorId: role === "generator" ? id : null,
        traderId: traderId,
        // Ensure a fallback ID exists for React keys if API doesn't provide one
        uniqueId: item.id || `wallet-${Math.random()}`,
      }));
    } catch (e) {
      console.error("Error fetching wallet charges", e);
      return [];
    }
  };

  const fetchContractCharges = async (
    contractId,
    counterName,
    consumerId,
    generatorId,
  ) => {
    try {
      const res = await getContractCharges(contractId);

      const consumerCharge =
        res?.data?.data?.consumerCharges
          ?.filter((ele) => ele?.frequency === "RECURRING")
          ?.map((charge) => ({
            ...charge,
            entityName: activeRole === "CONSUMER" ? "Self" : counterName,
            bilateralContractId: contractId,
            sourceType: "CONTRACT",
            // Attach IDs explicitly
            consumerId: consumerId,
            // generatorId: generatorId,
          })) || [];

      const generatorCharge =
        res?.data?.data?.generatorCharges
          ?.filter((ele) => ele?.frequency === "RECURRING")
          ?.map((charge) => ({
            ...charge,
            entityName: activeRole === "GENERATOR" ? "Self" : counterName,
            bilateralContractId: contractId,
            sourceType: "CONTRACT",
            // Attach IDs explicitly
            // consumerId: consumerId,
            generatorId: generatorId,
          })) || [];

      return [...consumerCharge, ...generatorCharge];
    } catch (e) {
      console.error("Error fetching contract charges", e);
      return [];
    }
  };
  // --- EFFECTS ---
  useEffect(() => {
    const loadAllData = async () => {
      try {
        setIsLoadingCharges(true); // Start loader
        const { selfId, counterIds, contractIds } = details || {};
        const allPromises = [];

        // 1. Contract Charges
        if (contractIds?.length > 0) {
          contractIds.forEach((ele) => {
            const counterName =
              activeRole === "CONSUMER"
                ? ele?.generatorName
                : ele?.consumerName;
            allPromises.push(
              fetchContractCharges(
                ele?.contractId,
                counterName,
                ele?.consumerId,
                ele?.generatorId,
              ),
            );
          });
        }

        // 2. Wallet Charges
        if (selfId) {
          const uniqueCounterIds = Array.isArray(counterIds)
            ? counterIds.filter(
                (item, index, arr) =>
                  arr.findIndex((i) => i.counterId === item.counterId) ===
                  index,
              )
            : [];

          if (activeRole === "CONSUMER") {
            if (!contractIds || contractIds.length === 0) {
              allPromises.push(fetchWalletCharges("consumer", selfId, "Self"));
            }
            uniqueCounterIds.forEach((ctr) => {
              if (ctr.counterId) {
                allPromises.push(
                  fetchWalletCharges(
                    "generator",
                    ctr.counterId,
                    ctr?.name,
                    ctr?.traderId,
                  ),
                );
              }
            });
          } else {
            if (!contractIds || contractIds.length === 0) {
              allPromises.push(fetchWalletCharges("generator", selfId, "Self"));
            }
            uniqueCounterIds.forEach((ctr) => {
              if (ctr.counterId) {
                allPromises.push(
                  fetchWalletCharges(
                    "consumer",
                    ctr.counterId,
                    ctr?.name,
                    ctr.traderId,
                  ),
                );
              }
            });
          }
        }

        const results = await Promise.all(allPromises);

        const flatResults = results.flat().filter((item) => {
          if (item.chargeType === "Trader") {
            if (item.entityName === "Self") return isSelfTraderUser;
            return !!item.traderId;
          }
          return true;
        });

        setChargeData(flatResults);
      } catch (error) {
        console.error("Failed to fetch data", error);
      } finally {
        setIsLoadingCharges(false); // Stop loader
      }
    };

    if (details) {
      loadAllData();
    }
  }, [activeRole, details, isSelfTraderUser]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- HANDLERS ---

  /**
   * Updates the value in the main state.
   * We pass the specific object 'itemToUpdate' to identify which one to modify.
   * We use .map() on the previous state to ensure immutability.
   */
  const handleChangeValue = (val, itemToUpdate) => {
    setChargeData((prevData) =>
      prevData.map((item) => {
        // Compare by reference (since itemToUpdate comes from the same array)
        // Or strictly by ID if your API guarantees unique IDs: if (item.id === itemToUpdate.id)
        if (item === itemToUpdate) {
          return { ...item, value: val };
        }
        return item;
      }),
    );
  };

  const handleSaveClick = () => {
    setConfirmModal({
      status: true,
      header:
        details?.contractIds?.length > 0
          ? "Approve Terms Confirmation"
          : "Finalize Terms Confirmation",
      data:
        details?.contractIds?.length > 0 ? (
          <>
            Once the Terms & Conditions are accepted, no further changes will be
            allowed. <b>Initial Obligation will be blocked.</b> Do you want to
            continue?
          </>
        ) : (
          "The T&C will be shared with the counterparty for final approval. All other pending connection requests will be auto-rejected."
        ),
      note:
        details?.contractIds?.length > 0
          ? ""
          : "Once the T&C are accepted, no further changes will be allowed. Initial Obligation will be blocked. Do you want to continue?",
    });
  };

  // --- RENDER HELPER ---
  // This extracts the row logic to avoid duplication
  const renderChargeRow = (
    ele,
    index,
    idx,
    isSelf = true,
    viewCharge = true,
  ) => {
    const isDisabled =
      (ele?.isAdminCharge && ele?.bilateralContractId) ||
      ele?.isAdminCharge ||
      ele?.bilateralContractId;

    return (
      <div key={index} className={"display_flex_row tableDetails"}>
        {viewCharge ? (
          <>
            <div>{ele?.name || "-"}</div>
            <div
              style={{
                width: "10%",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={isSelf ? "Self" : details?.counterIds[idx]?.name || "-"}
            >
              {isSelf ? "Self" : details?.counterIds[idx]?.name || "-"}
            </div>
            <div style={{ width: "15%" }}>{ele?.chargeType || "-"}</div>
            <div style={{ width: "20%" }} className={Styles.valueInputCont}>
              <input
                disabled={isDisabled}
                type="text"
                // Use fallback '' to prevent uncontrolled input warning
                value={ele?.value ?? ""}
                onChange={(e) => handleChangeValue(e.target.value, ele)}
              />
            </div>
            <div style={{ width: "10%" }}>
              {ele?.type === "PER_MW" ? "₹/kWh" : ele?.type}
            </div>
          </>
        ) : (
          <>
            <div><Skeleton variant="text" width={120} height={20} /></div>
            <div style={{ width: "10%" }}><Skeleton variant="text" width={60} height={20} /></div>
            <div style={{ width: "15%" }}><Skeleton variant="text" width={80} height={20} /></div>
            <div style={{ width: "20%" }}><Skeleton variant="text" width={100} height={20} /></div>
            <div style={{ width: "10%" }}><Skeleton variant="text" width={50} height={20} /></div>
          </>
        )}
      </div>
    );
  };

  return (
    <>
      {confirmModal?.status && (
        <ConfirmationModal
          open={confirmModal?.status}
          loading={loading}
          header={confirmModal?.header}
          data={confirmModal?.data}
          note={confirmModal?.note}
          handleClose={() =>
            setConfirmModal({ status: false, header: null, data: null })
          }
          handleSubmit={() => handleSubmit(chargeData)}
        />
      )}

      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="child-modal-title"
        aria-describedby="child-modal-description"
      >
        <div className={`popupModal ${Styles.modalMainChargeSelection}`}>
          {/* Loader Overlay */}
          {isLoadingCharges && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                background: "rgba(255,255,255,0.7)",
                zIndex: 10,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "10px",
              }}
            >
              <CircularProgress size={60} thickness={5} />
            </div>
          )}

          {/* Header */}
          <div className="modalHeading">
            <div style={{ color: "var(--fourth-color-text)", fontWeight: 500 }}>
              Charge Finalization
            </div>
            <Close onClick={handleClose} style={{ cursor: "pointer" }} />
          </div>

          {/* Table Content */}
          <div className={Styles.chargeMainElemCont}>
            {/* Table Header */}
            <div className={`display_flex_row tableHeading`}>
              <div style={{ textAlign: "left" }}>Charge Name</div>
              <div style={{ width: "10%" }}>Assignee</div>
              <div style={{ width: "15%" }}>Charge Type</div>
              <div style={{ width: "20%" }}>Value</div>
              <div style={{ width: "10%" }}>Type</div>
            </div>

            <div
              style={{ backgroundColor: "#dedfea" }}
              className={` ${Styles.chargeHeight}`}
            >
              {/* --- SECTION 1: SELF CHARGES --- */}
              <div>
                <div
                  style={{ backgroundColor: "#52888f" }}
                  className={"display_flex_row tableDetails"}
                >
                  <div
                    style={{
                      color: "white",
                      fontWeight: 600,
                    }}
                  >
                    Self Charges
                  </div>
                </div>
                {chargeData
                  ?.filter(
                    (e) =>
                      e?.entityName === "Self" &&
                      (isSelfTraderUser || e?.chargeType !== "Trader"),
                  )
                  .map((ele, index) => renderChargeRow(ele, index, null, true))}
              </div>

              {/* --- SECTION 2: COUNTER PARTY CHARGES --- */}
              {entityNames.length > 0 && (
              <div style={{ margin: "10px 0px" }}>
                <div
                  style={{ backgroundColor: "#52888f" }}
                  className={"display_flex_row tableDetails"}
                >
                  <div
                    style={{
                      color: "white",
                      fontWeight: 600,
                    }}
                  >
                    Counter Party Charges
                  </div>
                </div>

                {entityNames.map((entityName, idx) => (
                  <React.Fragment key={idx}>
                    <div
                      style={{ backgroundColor: "#effcff" }}
                      className={"display_flex_row tableDetails"}
                    >
                      <div
                        style={{
                          // width: "30%",
                          color: "#52888f",
                          fontWeight: 600,
                        }}
                      >
                        {entityName} Charges
                      </div>
                    </div>
                    {chargeData
                      ?.filter((e) => e?.entityName === entityName)
                      .filter(
                        (e) =>
                          !(e?.chargeType === "Trader" && !e?.traderId),
                      )
                      .map((ele, index) =>
                        renderChargeRow(
                          ele,
                          index,
                          idx,
                          false,
                          ele?.chargeType !== "Platform",
                        ),
                      )}
                  </React.Fragment>
                ))}
              </div>
              )}
            </div>
          </div>

          {/* Footer / Buttons */}
          <div className={Styles.buttonContainer}>
            {type === "accept" && (
              <button
                style={{
                  padding: "12px 24px",
                  backgroundColor: "var(--warning-background-thin) !important",
                  color: "var(--danger-shade-text)",
                  border: "1px solid var(--danger-shade-text)",
                  borderRadius: "10px",
                  fontWeight: 500,
                  cursor: "pointer",
                  marginRight: "10px",
                }}
                onClick={() => {
                  setRejectContract({
                    contractIds: mainScreenMatchData?.map((ele) => ({
                      contractId: ele?.id,
                      consumerId: ele?.capacityConsumer?.consumerId,
                      consumerName: ele?.capacityConsumer?.consumerName,
                      generatorId: ele?.capacityGenerator?.generatorId,
                      generatorName: ele?.capacityGenerator?.generatorName,
                    })),
                  });
                  setRejectAllTermsConfirm(true);
                }}
              >
                Reject All
              </button>
            )}
            <div
              className={`activeButton ${Styles.submitButton}`}
              onClick={handleSaveClick}
            >
              {type === "accept" ? "Approve" : "Submit"}
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ChargeSelection;
